/**
 * FeatureGate Component
 * Phase 10: Monetization
 *
 * Conditionally renders children based on feature access.
 * Shows an upgrade prompt if the feature is not available.
 */

import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FEATURE_DESCRIPTIONS } from '@/lib/planLimits';
import type { FeatureKey } from '@/types/billing.types';

interface FeatureGateProps {
  /** The feature to check */
  feature: FeatureKey;
  /** Content to render if feature is available */
  children: React.ReactNode;
  /** Custom fallback content when feature is not available */
  fallback?: React.ReactNode;
  /** Show inline badge instead of blocking */
  inline?: boolean;
  /** Custom message for the upgrade prompt */
  message?: string;
  /** Show loading skeleton while checking */
  showLoading?: boolean;
}

export function FeatureGate({
  feature,
  children,
  fallback,
  inline = false,
  message,
  showLoading = false,
}: FeatureGateProps) {
  const { hasFeature, isLoading } = useFeatureAccess();
  const navigate = useNavigate();

  if (isLoading && showLoading) {
    return (
      <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-24" />
    );
  }

  if (isLoading) {
    return null;
  }

  const hasAccess = hasFeature(feature);

  if (hasAccess) {
    return <>{children}</>;
  }

  // Custom fallback provided
  if (fallback) {
    return <>{fallback}</>;
  }

  // Inline mode: show badge next to content
  if (inline) {
    return (
      <InlineUpgradeBadge feature={feature} message={message}>
        {children}
      </InlineUpgradeBadge>
    );
  }

  // Default: show upgrade prompt
  return <UpgradePrompt feature={feature} message={message} />;
}

/**
 * Upgrade prompt shown when feature is not available
 */
interface UpgradePromptProps {
  feature: FeatureKey;
  message?: string;
  compact?: boolean;
}

export function UpgradePrompt({ feature, message, compact = false }: UpgradePromptProps) {
  const navigate = useNavigate();
  const featureDescription = FEATURE_DESCRIPTIONS[feature] || feature;

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
        <Lock className="w-5 h-5 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {message || `${featureDescription} - Fonctionnalite Premium`}
          </p>
        </div>
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate('/pricing')}
        >
          Upgrade
        </Button>
      </div>
    );
  }

  return (
    <Card className="p-8 text-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Lock className="w-8 h-8 text-primary" />
      </div>

      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Fonctionnalite Premium
      </h3>

      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
        {message || (
          <>
            <strong>{featureDescription}</strong> necessite un plan superieur.
            Passez a un plan premium pour debloquer cette fonctionnalite.
          </>
        )}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={() => navigate('/pricing')}>
          <Sparkles className="w-4 h-4 mr-2" />
          Voir les plans
        </Button>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Retour
        </Button>
      </div>
    </Card>
  );
}

/**
 * Inline badge for features that should be visible but locked
 */
interface InlineUpgradeBadgeProps {
  feature: FeatureKey;
  children: React.ReactNode;
  message?: string;
}

function InlineUpgradeBadge({ feature, children, message }: InlineUpgradeBadgeProps) {
  const navigate = useNavigate();
  const featureDescription = FEATURE_DESCRIPTIONS[feature] || feature;

  return (
    <div className="relative group">
      <div className="opacity-50 pointer-events-none select-none">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Lock className="w-4 h-4" />
          <span className="text-sm font-medium">
            {message || `Debloquer ${featureDescription}`}
          </span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Badge */}
      <div className="absolute -top-2 -right-2 bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
        Pro
      </div>
    </div>
  );
}

/**
 * Usage limit gate - blocks when usage limit is reached
 */
interface UsageLimitGateProps {
  metric: 'products' | 'users' | 'restaurants' | 'ai_requests';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function UsageLimitGate({ metric, children, fallback }: UsageLimitGateProps) {
  const { checkUsageLimit, isLoading } = useFeatureAccess();
  const navigate = useNavigate();

  // We need to check synchronously, so we'll use the cached value
  // In a real implementation, this would use a separate hook with cached data

  if (isLoading) {
    return null;
  }

  // For now, always allow - usage check should be done before action
  return <>{children}</>;
}

/**
 * Premium badge to show on features
 */
export function PremiumBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-full ${className}`}
    >
      <Sparkles className="w-3 h-3" />
      Pro
    </span>
  );
}

export default FeatureGate;
